import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { contratosService } from "../end-points/contratos";
import { useToast } from "@/hooks/use-toast";
import {
  ICreateContract,
  IUpdateContract,
  IApprovalDecisionRequest,
} from "@/interfaces/contracts.interface";

// ============================================
// QUERY KEY FACTORY (Advanced Pattern)
// ============================================
export const contratosKeys = {
  all: ['contratos'] as const,
  lists: () => [...contratosKeys.all, 'list'] as const,
  list: (movieId: number) => [...contratosKeys.lists(), { movieId }] as const,
  details: () => [...contratosKeys.all, 'detail'] as const,
  detail: (id: number) => [...contratosKeys.details(), id] as const,
  approvals: (id: number) => [...contratosKeys.detail(id), 'approvals'] as const,
};

// ============================================
// QUERY HOOKS
// ============================================

/**
 * Get all contracts for a movie
 * @param movieId - Movie/project ID
 */
export const useGetContractsByMovie = (movieId: number) => {
  return useQuery({
    queryKey: contratosKeys.list(movieId),
    queryFn: () => contratosService.getContractsByMovie(movieId),
    enabled: !!movieId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
};

/**
 * Get single contract by ID
 * @param id - Contract ID
 */
export const useGetContractById = (id: number) => {
  return useQuery({
    queryKey: contratosKeys.detail(id),
    queryFn: () => contratosService.getContractById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get approval history for a contract
 * @param id - Contract ID
 */
export const useGetApprovalHistory = (id: number) => {
  return useQuery({
    queryKey: contratosKeys.approvals(id),
    queryFn: () => contratosService.getApprovalHistory(id),
    enabled: !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// ============================================
// MUTATION HOOKS
// ============================================

/**
 * Create new contract
 */
export const useCreateContract = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: ICreateContract) => contratosService.createContract(data),
    onSuccess: (response, variables) => {
      // Invalidate movie's contract list
      queryClient.invalidateQueries({ queryKey: contratosKeys.list(variables.movieId) });

      toast({
        title: "Salvo",
        description: "Salvo com sucesso",
        variant: "success",
      });
    },
    onError: (error: Error) => {
      console.error("Error creating contract:", error);
      toast({
        title: "Erro ao criar contrato",
        description: error?.message || "Não foi possível criar o contrato",
        variant: "destructive",
      });
    }
  });
};

/**
 * Update existing contract (DRAFT only)
 */
export const useUpdateContract = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: IUpdateContract) => contratosService.updateContract(data),
    onSuccess: (response, variables) => {
      // Invalidate specific contract detail
      queryClient.invalidateQueries({ queryKey: contratosKeys.detail(variables.id) });

      // Invalidate movie list if movieId is available
      if (variables.movieId) {
        queryClient.invalidateQueries({ queryKey: contratosKeys.list(variables.movieId) });
      }

      toast({
        title: "Contrato atualizado",
        description: "Contrato atualizado com sucesso",
        variant: "success",
      });
    },
    onError: (error: Error) => {
      console.error("Error updating contract:", error);
      toast({
        title: "Erro ao atualizar contrato",
        description: error?.message || "Não foi possível atualizar o contrato",
        variant: "destructive",
      });
    }
  });
};

/**
 * Delete contract (DRAFT only)
 */
export const useDeleteContract = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: number) => contratosService.deleteContract(id),
    onSuccess: () => {
      // Invalidate all contract lists
      queryClient.invalidateQueries({ queryKey: contratosKeys.lists() });

      toast({
        title: "Contrato excluído",
        description: "Contrato excluído com sucesso",
        variant: "success",
      });
    },
    onError: (error: Error) => {
      console.error("Error deleting contract:", error);
      toast({
        title: "Erro ao excluir contrato",
        description: error?.message || "Não foi possível excluir o contrato",
        variant: "destructive",
      });
    }
  });
};

/**
 * Submit contract for review
 */
export const useSubmitContract = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: number) => contratosService.submitContract(id),
    onSuccess: (response, id) => {
      // Invalidate contract detail and lists
      queryClient.invalidateQueries({ queryKey: contratosKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: contratosKeys.lists() });

      toast({
        title: "Contrato enviado",
        description: "Contrato enviado para aprovação com sucesso",
        variant: "success",
      });
    },
    onError: (error: Error) => {
      console.error("Error submitting contract:", error);
      toast({
        title: "Erro ao enviar contrato",
        description: error?.message || "Não foi possível enviar o contrato para aprovação",
        variant: "destructive",
      });
    }
  });
};

/**
 * Approve/reject/request changes for contract
 */
export const useApproveContract = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, decision }: { id: number; decision: IApprovalDecisionRequest }) =>
      contratosService.approveContract(id, decision),
    onSuccess: (response, variables) => {
      // Invalidate contract detail, approvals, and lists
      queryClient.invalidateQueries({ queryKey: contratosKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: contratosKeys.approvals(variables.id) });
      queryClient.invalidateQueries({ queryKey: contratosKeys.lists() });

      const actionText = variables.decision.decision === 'APPROVED'
        ? 'aprovado'
        : variables.decision.decision === 'REJECTED'
        ? 'rejeitado'
        : 'retornado para ajustes';

      toast({
        title: `Contrato ${actionText}`,
        description: `Contrato ${actionText} com sucesso`,
        variant: "success",
      });
    },
    onError: (error: Error) => {
      console.error("Error processing approval:", error);
      toast({
        title: "Erro ao processar aprovação",
        description: error?.message || "Não foi possível processar a aprovação",
        variant: "destructive",
      });
    }
  });
};

/**
 * Contractor accepts contract
 */
export const useAcceptContract = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: number) => contratosService.acceptContract(id),
    onSuccess: (response, id) => {
      // Invalidate contract detail and lists
      queryClient.invalidateQueries({ queryKey: contratosKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: contratosKeys.lists() });

      toast({
        title: "Contrato aceito",
        description: "Você aceitou o contrato com sucesso",
        variant: "success",
      });
    },
    onError: (error: Error) => {
      console.error("Error accepting contract:", error);
      toast({
        title: "Erro ao aceitar contrato",
        description: error?.message || "Não foi possível aceitar o contrato",
        variant: "destructive",
      });
    }
  });
};

/**
 * Activate signed contract (triggers blockchain)
 */
export const useActivateContract = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: number) => contratosService.activateContract(id),
    onSuccess: (response, id) => {
      // Invalidate contract detail and lists
      queryClient.invalidateQueries({ queryKey: contratosKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: contratosKeys.lists() });

      toast({
        title: "Contrato ativado",
        description: "Contrato ativado e registrado no blockchain com sucesso",
        variant: "success",
      });
    },
    onError: (error: Error) => {
      console.error("Error activating contract:", error);
      toast({
        title: "Erro ao ativar contrato",
        description: error?.message || "Não foi possível ativar o contrato",
        variant: "destructive",
      });
    }
  });
};

/**
 * Upload contract document (PDF)
 */
export const useUploadContractDocument = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: (formData: FormData) => contratosService.uploadContractDocument(formData),
    onError: (error: Error) => {
      console.error("Error uploading contract document:", error);
      toast({
        title: "Erro ao enviar documento",
        description: error?.message || "Não foi possível enviar o documento",
        variant: "destructive",
      });
    }
  });
};
